// Sentry — precisa rodar antes de qualquer outro código (primeiro import do main.tsx).
import * as Sentry from '@sentry/react'

const RELEASE = __RELEASE__ as string

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
  environment: import.meta.env.MODE,
  release: RELEASE,
  integrations: [
    Sentry.browserTracingIntegration(),
    // ReplayIntegration sem feedbackIntegration — o widget de feedback do Sentry
    // não está habilitado na UI (não há botão injetado). Removê-lo evita ~50 kB
    // no bundle. Se for ligar o widget no futuro, re-adicionar passando
    // `buttonLabel`/`autoInject`/`colorScheme` conforme a UX desejada.
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
    Sentry.httpClientIntegration(),
  ],
  // 1.0 em dev/homolog (tracing completo pra debug), 0.1 em prod (10% da amostragem
  // para conter custo — 100% era proibitivo em prod com vários operadores simultâneos).
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/api(\.\w+)?\.filazero\.net/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 0.5,
  debug: import.meta.env.MODE !== 'production',
  beforeSend(event, hint) {
    // Filter out noisy errors
    const error = hint?.originalException
    if (error instanceof Error) {
      const noisy = [
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        'Script error',
        'NetworkError',
        'Failed to fetch',
      ]
      if (noisy.some((n) => error.message.includes(n))) {
        return null
      }
    }
    // Sanitize PII
    if (event.request?.url) {
      event.request.url = event.request.url.replace(
        /(token|password|secret|api[_-]?key|authorization)=[^&]+/gi,
        '$1=[REDACTED]'
      )
    }
    if (event.contexts?.trace?.data?.url) {
      event.contexts.trace.data.url = event.contexts.trace.data.url.replace(
        /(token|password|secret|api[_-]?key|authorization)=[^&]+/gi,
        '$1=[REDACTED]'
      )
    }
    return event
  },
})

// Global error handlers for non-React errors
window.addEventListener('error', (e) => {
  Sentry.captureException(e.error ?? new Error(e.message), { level: 'error' })
})
window.addEventListener('unhandledrejection', (e) => {
  Sentry.captureException(e.reason ?? new Error(String(e.reason)), { level: 'error' })
})

// Helper exports for use throughout the app
export function setUserContext(user: { id: string; email?: string; username?: string; data?: Record<string, unknown> } | null) {
  if (user) {
    Sentry.setUser(user)
  } else {
    Sentry.setUser(null)
  }
}

export function setTags(tags: Record<string, string | null>) {
  Object.entries(tags).forEach(([k, v]) => Sentry.setTag(k, v))
}

export function captureApiError(error: Error, context: Record<string, unknown>) {
  Sentry.captureException(error, { tags: { api_error: 'true', ...context }, level: 'error' })
}

export function captureException(error: Error, extra?: Record<string, unknown>) {
  Sentry.captureException(error, extra)
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level)
}

export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb)
}

export function startTransaction(name: string, op: string) {
  return Sentry.startInactiveSpan({ name, op })
}